import {
  readFromLocalStorage,
  removeFromLocalStorage,
  writeToLocalStorage,
} from '@jouzen/ecom-utils';
import {
  combineReducers,
  configureStore,
  createAction,
} from '@reduxjs/toolkit';

import accountReducer from './account/accountSlice';
import eligibilityReducer from './eligibility/eligibilitySlice';
import orderReducer from './order/orderSlice';
import redeemReducer from './redeem/redeemSlice';

const localStorageMiddleware = ({ getState }: any) => {
  return (next: (arg0: any) => any) => (action: any) => {
    const result = next(action);
    writeToLocalStorage('applicationState', JSON.stringify(getState()));
    return result;
  };
};

const combinedReducer = combineReducers({
  order: orderReducer,
  account: accountReducer,
  redeem: redeemReducer,
  eligibility: eligibilityReducer,
});

export type RootState = ReturnType<typeof combinedReducer>;

/**
 * Dispatched once on the client after mount to fill the store from the
 * snapshot persisted in localStorage. The store deliberately boots from
 * the empty initial state so the server render and the client's first
 * render agree (avoiding the React #423 hydration mismatch); `hydrate`
 * then merges the persisted slices in on the next tick. See StoreProvider.
 */
export const hydrate = createAction<Partial<RootState>>('hydrate');

const rootReducer = (
  state: RootState | undefined,
  action: Parameters<typeof combinedReducer>[1],
): RootState => {
  if (hydrate.match(action)) {
    // Top-level merge: slices missing from the snapshot keep their
    // initial state rather than being clobbered with `undefined`.
    return { ...(state as RootState), ...action.payload };
  }
  return combinedReducer(state, action);
};

/**
 * Reads the persisted redux snapshot from localStorage. Returns
 * `undefined` on the server (no localStorage) or when nothing has been
 * persisted yet, in which case the store keeps its initial state.
 *
 * A corrupted or legacy snapshot (invalid JSON) is discarded rather than
 * thrown: parsing errors here would otherwise propagate out of the
 * StoreProvider effect and leave the app stuck behind the loading shell.
 * The bad key is cleared so the failure can't recur on the next load.
 */
export const loadPersistedState = (): Partial<RootState> | undefined => {
  const localState = readFromLocalStorage('applicationState');
  if (!localState) {
    return undefined;
  }

  try {
    return JSON.parse(localState);
  } catch {
    removeFromLocalStorage('applicationState');
    return undefined;
  }
};

export const makeStore = () => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(localStorageMiddleware),
  });
};

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore['dispatch'];
